import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportReactError } from "@/lib/errorReporter";
import i18n from "@/i18n";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    reportReactError(error, {
      componentStack: info.componentStack ?? undefined,
      boundary: "AppErrorBoundary",
    });
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = import.meta.env?.BASE_URL ?? "/";
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          padding: "2rem",
          textAlign: "center",
          backgroundColor: "#0b0b0c",
          color: "#f5f5f5",
          fontFamily:
            "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 600, margin: 0 }}>
          {i18n.t("app:errorBoundary.title")}
        </h1>
        <p style={{ maxWidth: "32rem", margin: 0, color: "#a1a1aa" }}>
          {i18n.t("app:errorBoundary.description")}
        </p>
        {import.meta.env?.DEV && this.state.error.message ? (
          <pre
            style={{
              maxWidth: "40rem",
              overflow: "auto",
              padding: "0.75rem 1rem",
              borderRadius: "0.5rem",
              backgroundColor: "#1a1a1d",
              color: "#fca5a5",
              fontSize: "0.8rem",
              textAlign: "left",
            }}
          >
            {this.state.error.message}
          </pre>
        ) : null}
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "1px solid #3f3f46",
              backgroundColor: "#27272a",
              color: "#f5f5f5",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {i18n.t("app:errorBoundary.reload")}
          </button>
          <button
            type="button"
            onClick={this.handleGoHome}
            style={{
              padding: "0.5rem 1rem",
              borderRadius: "0.375rem",
              border: "1px solid #3f3f46",
              backgroundColor: "transparent",
              color: "#f5f5f5",
              cursor: "pointer",
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {i18n.t("app:errorBoundary.goHome")}
          </button>
        </div>
      </div>
    );
  }
}
